import {
  industrySlugs,
  type CapabilitySlug,
  type IndustrySlug,
  type SeoContent,
} from "./contentSchema";

export { industrySlugs };
export type { IndustrySlug };

export type IndustryRecord = SeoContent & {
  slug: IndustrySlug;
  image?: string;
  title: string;
  headline: string;
  summary: string;
  heroCopy: string[];
  operatingReality: {
    title: string;
    copy: string[];
  };
  engineeringPriorities: {
    title: string;
    description: string;
  }[];
  applicableWork: string[];
  experienceIntro?: string;
  experience: {
    title: string;
    description: string;
  }[];
  perspective: {
    headline: string;
    copy: string;
  };
  cta: string;
  relatedCapabilities: CapabilitySlug[];
};

export const industriesIndexPage = {
  image: "/images/industries/industries-index-hero.webp",
  seoTitle: "Industries | General Applications",
  seoDescription:
    "General Applications engineers software systems for energy, agriculture, enterprise, healthcare, military, and media operations.",
  headline: "The system should fit the work, **not the other way around**.",
  heroCopy: [
    "Every operating environment develops its own constraints, sources of authority, failure modes, and definitions of success. A control room, clinical workflow, maintenance program, farm operation, enterprise platform, and media pipeline may use similar technical tools, but they cannot be engineered as though the work were interchangeable.",
    "General Applications brings disciplined systems engineering to each environment: establishing trustworthy information, explicit workflow state, controlled automation, and software that remains understandable when real operations stop matching the ideal diagram.",
  ],
  introTitle: "Different environments, **the same engineering discipline**.",
  introCopy: [
    "A generating unit, a clinical workflow, and a farm operation share no equipment, regulations, or failure modes — but they share the same underlying condition: fragmented information, brittle processes, and systems that were never built to talk to each other.",
    "Each page below describes how that condition shows up in that specific environment, in its own terms and constraints, not a single technology stack applied uniformly across all of them.",
    "What stays constant across every environment is the approach: understand the operating reality first, then engineer software that respects it — rather than software that assumes the reality away.",
  ],
} as const;

export const industries: IndustryRecord[] = [
  {
    slug: "energy",
    title: "Energy",
    seoTitle: "Energy Systems | General Applications",
    seoDescription:
      "General Applications designs operational software, data systems, and automation tooling for power generation, controls, and critical infrastructure.",
    headline:
      "Engineered by people who have walked the unit, not just modeled it.",
    summary:
      "Operational software and information infrastructure shaped by physical equipment, safety constraints, maintenance realities, and regulatory obligations.",
    image: "/images/industries/energy-hero.webp",
    heroCopy: [
      "In a generating unit or substation, you cannot containerize a boiler trip or abstract away the physics of the process.",
      "Equipment behavior, control logic, protective systems, maintenance windows, and regulatory compliance are not edge cases to design around later — they are the starting constraints, and the software gets engineered to them, not squeezed inside them.",
    ],
    operatingReality: {
      title: "Operational context is part of the architecture",
      copy: [
        "A generating unit, substation, or control network is not simply another collection of APIs. It is part of a larger physical process with established protections, operating procedures, system authorities, and consequences for getting it wrong.",
        "Modernization succeeds when software respects those constraints while making information easier to trust, trace, and act on — usually by integrating long-lived control and historian systems with newer data, workflow, and decision-support tools rather than replacing everything at once.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Operational continuity",
        description:
          "Design around unit state, outage schedules, maintenance access, and change controls so modernization does not interrupt work that cannot be casually paused.",
      },
      {
        title: "Authoritative operational data",
        description:
          "Reconcile asset, configuration, alarm, and event data while preserving lineage, timestamps, and source authority, so every value can be traced back to the system that owns it.",
      },
      {
        title: "Clear operator decisions",
        description:
          "Present equipment state, exceptions, and required actions with enough context for operators and technicians to act confidently under time pressure.",
      },
      {
        title: "Controlled system integration",
        description:
          "Connect control, historian, engineering, maintenance, and enterprise systems through deliberate interfaces rather than fragile point-to-point automation.",
      },
    ],
    applicableWork: [
      "Control-system design, modernization, migration, and owner's-engineer support",
      "P&ID, control-logic, instrumentation, I/O, and design-package review",
      "Plant walkdowns, field verification, project definition, and vendor evaluation",
      "Control-room and HMI design, operator dashboards, and alarm/exception-management tooling",
      "Control-system, historian, asset, maintenance, and enterprise integration",
      "Emissions monitoring, pollution-control, and environmental-compliance systems",
      "Industrial cybersecurity, configuration-baseline, and change-control management",
      "Search and retrieval across procedures, drawings, records, and technical documentation",
    ],
    experienceIntro:
      "General Applications draws on founder experience across power generation, industrial controls, operational dashboards, and critical-infrastructure cybersecurity engineering.",
    experience: [
      {
        title: "Power generation and control-system engineering",
        description:
          "Built and reviewed control-system designs across fossil and nuclear generation, including migrations, burner-management replacement, auxiliary boiler modernization, plant instrumentation, control-room interfaces, emissions-control systems, fire protection, technical specifications, and owner's-engineering support spanning design review, vendor evaluation, field verification, and technical decision support.",
      },
      {
        title: "Industrial control and operational integration",
        description:
          "Worked directly with PLC, DCS, and SCADA platforms, including alarm, archive, and configuration systems, across power generation, mining, research, and water-treatment environments.",
      },
      {
        title: "Control-room and operational dashboard design",
        description:
          "Built control-room and video-wall dashboard software, including flexible graphing, indicator groups, and operations-planning tools, along with operator-console and control-room interface replacements across fossil and nuclear plants.",
      },
      {
        title: "Critical-infrastructure cybersecurity",
        description:
          "Delivered NERC CIP and NIST-aligned engineering across fleetwide asset classification, network mapping, electronic security boundaries, configuration baselines, vulnerability assessment, hardening, and audit support.",
      },
    ],
    perspective: {
      headline:
        "Modernization should make the operation more understandable, not merely more digital.",
      copy: "New software earns its place in an energy environment when it improves traceability, strengthens control, and helps the people responsible for the equipment make better decisions. Technology that hides operational reality behind fashionable abstractions is not modernization — it is deferred confusion.",
    },
    cta: "Discuss an Energy System",
    relatedCapabilities: [
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
  {
    slug: "agriculture",
    image: "/images/industries/agriculture-hero.webp",
    title: "Agriculture",
    seoTitle: "Agriculture Software & Farm Planning | General Applications",
    seoDescription:
      "General Applications builds farm-planning, crop-knowledge, monitoring, and decision-support systems for agricultural operations.",
    headline:
      "Agricultural systems should organize complexity without pretending the field is predictable or the grower is replaceable.",
    summary:
      "Farm-planning, crop-knowledge, monitoring, and decision-support systems built around biological timing, changing conditions, and the practical knowledge of growers.",
    heroCopy: [
      "Agricultural work connects long-range planning with daily decisions: what to grow, where to place it, when to start it, what conditions changed, what needs attention, and what can be harvested.",
      "Useful software brings those decisions, observations, schedules, and reference materials into one coherent system without reducing weather, biology, and local experience to a falsely precise formula.",
    ],
    operatingReality: {
      title: "The plan changes because the growing system changes",
      copy: [
        "Planting schedules, crop rotations, greenhouse starts, field tasks, inputs, pest pressure, weather, and harvest timing are interdependent. A change in one area can reshape the work everywhere else.",
        "The software has to preserve the original plan, capture what actually happened, and help growers understand the difference. That requires structured information, flexible workflows, and clear treatment of observations, estimates, and recommendations.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Connected planning",
        description:
          "Relate fields, beds, crops, varieties, planting windows, successions, tasks, inputs, and harvest expectations instead of managing each as an isolated list.",
      },
      {
        title: "Operational records",
        description:
          "Capture field observations, completed work, changing conditions, and actual outcomes so each season produces more useful information for the next.",
      },
      {
        title: "Structured crop knowledge",
        description:
          "Organize crop, variety, planting, care, pest, and harvest information into searchable models that can support planning, comparison, and reuse.",
      },
      {
        title: "Decision support with provenance",
        description:
          "Combine weather, reference material, operational data, and grower input while showing where recommendations came from and where judgment is still required.",
      },
    ],
    applicableWork: [
      "Farm, market-garden, and greenhouse planning platforms",
      "Field, bed, crop, variety, and succession-planning tools",
      "Planting calendars, task scheduling, and operational recordkeeping",
      "Crop, seed, planting, pest, and harvest knowledge systems",
      "Search and retrieval across agricultural references and farm records",
      "AI-assisted research, comparison, and structured content generation",
      "Weather, sensor, observation, and field-data integration",
      "Monitoring dashboards, exception workflows, and operational automation",
    ],
    experienceIntro:
      "General Applications draws on founder experience across agricultural planning, crop knowledge, operational records, and decision-support workflow engineering.",
    experience: [
      {
        title: "Agricultural planning systems",
        description:
          "Built farm-planning systems that connect fields, beds, crops, plantings, schedules, and seasonal workflows instead of leaving each decision trapped in a separate spreadsheet or notebook.",
      },
      {
        title: "Structured crop knowledge",
        description:
          "Structured crop, seed, planting, pest, and harvest knowledge into models that support search, comparison, planning, and AI-assisted workflows without losing source context.",
      },
      {
        title: "Operational record systems",
        description:
          "Built operational record systems for field observations, completed work, changing conditions, and actual outcomes so the next decision is informed by more than memory alone.",
      },
      {
        title: "Decision-support and workflow engineering",
        description:
          "Applied data modeling, reconciliation, workflow design, monitoring, search, and human-in-the-loop automation to agricultural operations where timing, variability, and local judgment all matter.",
      },
    ],
    perspective: {
      headline:
        "Good agricultural software remembers the plan, records reality, and helps explain the difference.",
      copy: "The goal is not to turn farming into a deterministic spreadsheet. It is to give growers a dependable operational memory: what was intended, what occurred, what changed, and what the available evidence suggests doing next.",
    },
    cta: "Discuss an Agricultural System",
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
  {
    slug: "enterprise",
    image: "/images/industries/enterprise-hero.webp",
    title: "Enterprise",
    seoTitle:
      "Enterprise Data, Search & Software Systems | General Applications",
    seoDescription:
      "General Applications designs enterprise data, search, workflow, compliance, and internal platform systems that reconcile fragmented information.",
    headline:
      "When systems disagree, the architecture has to establish what the business can trust.",
    summary:
      "Data, search, workflow, compliance, and internal platform systems that reconcile fragmented information across enterprise applications.",
    heroCopy: [
      "Enterprise environments accumulate applications faster than they establish shared meaning. Asset records, policies, security findings, operational data, documents, and business rules become distributed across systems that were never designed to agree.",
      "The engineering challenge is not simply connecting those systems. It is defining authority, validating what crosses each boundary, preserving lineage, and giving people a dependable model they can search, automate, and build on.",
    ],
    operatingReality: {
      title: "Integration does not create agreement by itself",
      copy: [
        "Connecting two applications only moves disagreement faster when identifiers, schemas, ownership, validation rules, and update behavior remain undefined. The pipeline may run successfully while the business still cannot trust its output.",
        "Reliable enterprise systems make those decisions explicit: which source owns each field, how conflicts are resolved, what changes are accepted, where exceptions go, and how the final result can be traced back to its origin.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Authoritative shared models",
        description:
          "Define stable models across assets, controls, documents, users, systems, and operational records so downstream applications do not each invent their own interpretation.",
      },
      {
        title: "Validated data movement",
        description:
          "Build ingestion and integration pipelines with schema validation, change detection, reconciliation, exception handling, and observable outcomes rather than silent data transfer.",
      },
      {
        title: "Searchable enterprise knowledge",
        description:
          "Make structured records and unstructured documents retrievable through search, filters, relationships, taxonomies, and grounded retrieval that preserves source context.",
      },
      {
        title: "Maintainable operational platforms",
        description:
          "Modernize internal applications, APIs, workflows, and administrative interfaces without interrupting the business processes already depending on them.",
      },
    ],
    applicableWork: [
      "Enterprise data modeling, reconciliation, enrichment, and governance",
      "Data ingestion, synchronization, validation, and integration pipelines",
      "Internal search, retrieval, taxonomy, and knowledge systems",
      "Compliance metrics, control mapping, evidence, and audit-support platforms",
      "Asset, configuration, security, and operational information systems",
      "APIs, databases, workflow systems, and administrative tools",
      "Network, storage, permissions, and infrastructure management systems",
      "AI readiness, grounded retrieval, and human-reviewed automation",
    ],
    experienceIntro:
      "General Applications draws on founder experience across enterprise data reconciliation, search and knowledge systems, compliance platforms, internal software, and infrastructure information systems.",
    experience: [
      {
        title: "Enterprise data reconciliation",
        description:
          "Developed systems that normalize, validate, deduplicate, enrich, and reconcile records across disconnected management, network, security, and operational platforms while preserving source authority and change history.",
      },
      {
        title: "Enterprise search and knowledge systems",
        description:
          "Designed search and knowledge systems for structured records and unstructured documents, including taxonomies, relationships, retrieval workflows, and interfaces that help users reach reliable answers across fragmented sources.",
      },
      {
        title: "Compliance and control-mapping platforms",
        description:
          "Built systems that connect individual controls, metrics, supporting evidence, and recognized frameworks so reporting remains traceable from technical detail through management and audit views.",
      },
      {
        title: "Internal platforms and workflow systems",
        description:
          "Architected database-backed applications, APIs, typed data layers, role-based access controls, workflow tools, and reusable administrative interfaces for business and operational teams.",
      },
      {
        title: "Infrastructure information systems",
        description:
          "Developed systems for asset and network mapping, security-boundary analysis, permissions administration, storage management, system monitoring, and other infrastructure workflows where accuracy and operability matter.",
      },
    ],
    perspective: {
      headline:
        "The enterprise does not need another source of truth. It needs a defensible way to establish one.",
      copy: "A trustworthy enterprise platform does more than gather records into one location. It defines authority, exposes disagreement, preserves provenance, and gives every downstream search, workflow, report, and AI system a foundation that can withstand scrutiny.",
    },
    cta: "Discuss an Enterprise System",
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
  {
    slug: "healthcare",
    image: "/images/industries/healthcare-hero.webp",
    title: "Healthcare",
    seoTitle: "Healthcare Software & Systems | General Applications",
    seoDescription:
      "General Applications designs healthcare workflow, device, documentation, and operational systems for regulated environments.",
    headline:
      "When care depends on software, the workflow has to stay clear under pressure.",
    summary:
      "Clinical, operational, and product systems shaped around regulated workflows, device inputs, documentation demands, and the realities of care delivery.",
    heroCopy: [
      "Healthcare environments combine time pressure, regulation, device complexity, documentation requirements, and workflows that do not pause just because the software becomes awkward.",
      "The engineering challenge is not only adding features. It is making forms, measurements, device behavior, alerts, and operational handoffs predictable enough that clinicians, support teams, and administrators can trust the system while work is still moving.",
    ],
    operatingReality: {
      title: "In clinical systems, friction becomes operational risk",
      copy: [
        "A save failure, ambiguous measurement, brittle device workflow, or exception path that nobody can untangle is not a minor product defect in healthcare. It becomes lost time, rework, documentation gaps, support burden, and reduced confidence in the system when people need clarity.",
        "Reliable healthcare software has to preserve consistent behavior across forms, measurements, devices, roles, and edge cases so the product can evolve without degrading workflows teams already depend on.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Workflow reliability under interruption",
        description:
          "Design forms, state handling, autosave, validation, and recovery so interruptions do not quietly become lost work or incomplete documentation.",
      },
      {
        title: "Device and measurement clarity",
        description:
          "Present measurements and device status so users can understand what they are seeing quickly and act without second-guessing the interface.",
      },
      {
        title: "Consistent regulated application behavior",
        description:
          "Build shared interaction patterns and component foundations so new workflows inherit proven behavior instead of reintroducing avoidable inconsistency.",
      },
      {
        title: "Maintainable operational platforms",
        description:
          "Keep healthcare products, internal tools, and supporting workflows maintainable as requirements grow across clinical, support, and administrative use cases.",
      },
    ],
    applicableWork: [
      "Clinical workflow, documentation, and form systems",
      "Device-management, measurement, and monitoring interfaces",
      "Operational tooling for care, support, and administrative teams",
      "Healthcare product architecture and application modernization",
      "Shared component, interaction, and validation foundations",
      "Decision-support, alerting, and information-flow improvements",
      "Regulated software behavior, traceability, and supportability",
      "Internal APIs, workflow tools, and platform systems for healthcare operations",
    ],
    experienceIntro:
      "General Applications draws on founder experience across healthcare product architecture, regulated workflows, device and measurement interfaces, and operational reliability work.",
    experience: [
      {
        title: "Clinical application architecture",
        description:
          "Led application architecture for a healthcare product, including systems for documentation, measurement capture, device management, autosave, validation, and workflow recovery.",
      },
      {
        title: "Reusable regulated-workflow foundations",
        description:
          "Designed a shared component architecture spanning forms, tables, dashboards, alerts, navigation, and other interaction patterns used throughout the product.",
      },
      {
        title: "Device, measurement, and documentation workflows",
        description:
          "Built interfaces where device inputs, measurements, documentation state, and exception handling had to remain understandable and recoverable across interruptions and edge cases.",
      },
      {
        title: "Supportability and operational reliability",
        description:
          "Stabilized product behavior and support workflows to reduce backlog, shorten response cycles, and make the system easier to operate and extend.",
      },
    ],
    perspective: {
      headline:
        "In healthcare, software quality is part of the operating environment, not a layer sitting above it.",
      copy: "A confusing workflow, silent state failure, or inconsistent device interaction does not remain contained as a user-experience problem for long. In regulated healthcare environments, software behavior affects documentation quality, operational trust, support load, and the attention people have available for the work that matters.",
    },
    cta: "Discuss a Healthcare System",
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
  {
    slug: "military",
    image: "/images/industries/military-hero.webp",
    title: "Military",
    seoTitle: "Military Maintenance & Readiness Systems | General Applications",
    seoDescription:
      "General Applications designs military maintenance, technical data, deployment, and readiness systems for controlled environments.",
    headline:
      "Maintenance and readiness systems fail when technical data, configuration, and field execution drift apart.",
    summary:
      "Technical-data, maintenance, deployment, and readiness systems built for controlled military environments where authority, traceability, and reliable field behavior carry operational consequences.",
    heroCopy: [
      "Military maintenance depends on more than technical manuals. It depends on authoritative information systems that connect procedures, parts, configurations, software, and field execution.",
      "The engineering challenge is preserving that authority while making the right information easier to validate, deploy, navigate, search, and act on under controlled and sometimes disconnected operating conditions.",
    ],
    operatingReality: {
      title:
        "The fielded system includes the content, software, and deployment chain",
      copy: [
        "A maintenance procedure can be correct at its source and still fail operationally if effectivity is unclear, the wrong revision reaches a device, a parts reference cannot be traced, or the application behaves differently across fielded configurations.",
        "Modernization therefore has to protect the entire chain: structured technical data, validation rules, publication workflows, application behavior, software baselines, deployment controls, support processes, and the technician experience at the point of use.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Authoritative technical data",
        description:
          "Preserve structured relationships among procedures, parts, applicability, configuration, revisions, warnings, references, and supporting media instead of flattening them into generic documents.",
      },
      {
        title: "Field-usable maintenance workflows",
        description:
          "Build model-based navigation, procedural interfaces, smart forms, exception handling, and status workflows that help technicians reach and complete the correct work without unnecessary ambiguity.",
      },
      {
        title: "Controlled and resilient deployment",
        description:
          "Design installers, updates, configuration baselines, support tooling, and application behavior for secured, fleet-scale, intermittently connected, or otherwise controlled environments.",
      },
      {
        title: "Traceable modernization",
        description:
          "Convert legacy technical-data and maintenance systems into modern, searchable platforms while preserving authority, revision history, validation evidence, and operational continuity.",
      },
    ],
    applicableWork: [
      "S1000D parsing, validation, transformation, and web or IETP publication systems",
      "Technical-publication and structured-content conversion platforms",
      "Interactive parts, procedure, model, and technical-reference interfaces",
      "Maintenance planning, execution, exception, status, and sustainment workflows",
      "Equipment configuration, effectivity, revision, and software-baseline systems",
      "Equipment, parts, sustainment, and maintenance-information systems",
      "Controlled installer, update, deployment, and fleet-support tooling",
      "Search and grounded AI retrieval across manuals, parts data, procedures, and maintenance records",
      "Training, qualification, and procedural knowledge systems",
      "Modernization of legacy maintenance, logistics, and technical-information applications",
      "Operational support workflows for secured or disconnected environments",
    ],
    experienceIntro:
      "General Applications draws on founder experience across military maintenance software, structured technical publications, controlled deployments, and operational systems engineering.",
    experience: [
      {
        title: "Military aircraft maintenance modernization",
        description:
          "Led the conversion of a military aircraft maintenance application for a U.S. Air Force program, including S1000D technical content, procedural smart forms, database interfaces, and interactive 2D/3D navigation with discrete part drill-downs.",
      },
      {
        title: "Structured technical-data systems",
        description:
          "Engineered S1000D content workflows and now applies that experience to General Applications development of parsing, validation, transformation, and web-publication tooling for structured military technical data.",
      },
      {
        title: "Interactive maintenance interfaces",
        description:
          "Built technical-reference systems combining model-based navigation, procedural workflows, and interactive technical content across PDF, SVG, Canvas, and 3D representations.",
      },
      {
        title: "Controlled fleet deployment",
        description:
          "Built installer and deployment tooling for maintenance software fielded across more than 10,000 secured military-grade devices, with supporting IT, security, and operational support work.",
      },
      {
        title: "Security and configuration discipline",
        description:
          "Additional experience in asset inventories, network mapping, configuration baselines, vulnerability assessment, access boundaries, and change control informs work in controlled military environments.",
      },
    ],
    perspective: {
      headline:
        "Readiness depends on whether authoritative information survives the journey from source data to field execution.",
      copy: "A correct procedure buried in the wrong revision, an untraceable part reference, or an update that cannot be reliably fielded is not merely a documentation defect. Military systems earn trust when technical data, configuration, software behavior, and deployment controls remain aligned under real operational conditions.",
    },
    cta: "Discuss a Military System",
    relatedCapabilities: [
      "intelligent-systems",
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
  {
    slug: "media-entertainment",
    image: "/images/industries/media-entertainment-hero.webp",
    title: "Media/Entertainment",
    seoTitle: "Media & Content Systems | General Applications",
    seoDescription:
      "General Applications designs media workflow, storage, monitoring, and platform systems for high-throughput production environments.",
    headline:
      "Creative throughput breaks down when storage, permissions, and operational tooling cannot keep up with the work.",
    summary:
      "Workflow, storage, monitoring, and platform systems for media environments where large-file operations and operational reliability shape the pace of delivery.",
    heroCopy: [
      "Media environments do not run on creative software alone. They depend on information systems that manage storage, permissions, metadata, monitoring, and operational workflows while large volumes of content move through the organization.",
      "The engineering challenge is making that infrastructure understandable and operable for the people responsible for keeping work in motion, while preserving the throughput, visibility, and control the environment needs underneath.",
    ],
    operatingReality: {
      title: "The infrastructure layer becomes part of the production process",
      copy: [
        "In media environments, creative work slows down long before a user files a ticket saying the storage platform is the problem. It shows up as stalled transfers, unclear permissions, capacity surprises, support friction, and teams working around the system instead of through it.",
        "Reliable media systems therefore need more than backend performance. They need clear administration, predictable file operations, visible system health, and workflows that let non-engineering teams operate the platform without constant escalation.",
      ],
    },
    engineeringPriorities: [
      {
        title: "Throughput reliability",
        description:
          "Design storage and file operations so large-file, high-concurrency workloads do not become the bottleneck in production workflows.",
      },
      {
        title: "Clear operational control",
        description:
          "Build administration interfaces for permissions, users, settings, and routine maintenance that operational teams can actually run day to day.",
      },
      {
        title: "Visible platform health",
        description:
          "Surface utilization, capacity, performance, and system health early enough to prevent outages, workflow disruption, and avoidable support escalation.",
      },
      {
        title: "Product-grade platform design",
        description:
          "Treat specialized infrastructure as a delivered product with coherent software, hardware integration, support expectations, and operational boundaries.",
      },
    ],
    applicableWork: [
      "Storage-platform and infrastructure-management software",
      "Filesystem, user, permissions, and settings administration interfaces",
      "Monitoring, analytics, and alerting for system health and utilization",
      "Workflow tooling for ingest, transfer, review, and delivery operations",
      "Search and retrieval foundations for large media and operational records",
      "Product architecture for specialized hardware-software platforms",
      "Support, reseller, and administrative systems around shipped platforms",
      "Reliability, deployment, and operational foundations for media infrastructure",
    ],
    experienceIntro:
      "General Applications draws on founder experience across storage-platform software, monitoring and analytics, operational administration, and hardware-software product design for media environments.",
    experience: [
      {
        title: "Storage-platform software",
        description:
          "Designed and built the full software suite for a storage-appliance product, including filesystem operations, user and permissions administration, system settings, and the operational interfaces required to manage it.",
      },
      {
        title: "Monitoring and utilization analytics",
        description:
          "Built live and historical analytics for CPU, memory, network, and related platform utilization across deployed appliance fleets.",
      },
      {
        title: "Hardware-software product architecture",
        description:
          "Designed the physical appliance alongside its software, with the support, warranty, and reseller structures required to ship and operate it as a real product.",
      },
      {
        title: "Operational platform administration",
        description:
          "Built systems where permissions, user management, storage behavior, monitoring visibility, and supportability all had to work together for non-engineering operators in high-throughput environments.",
      },
    ],
    perspective: {
      headline:
        "In media environments, infrastructure quality shows up as creative throughput.",
      copy: "When large files, specialized storage, and shared operational tools sit underneath the creative process, reliability is not background engineering work. It shapes whether teams can move, find issues early, and keep delivery on schedule without unnecessary friction.",
    },
    cta: "Discuss a Media System",
    relatedCapabilities: [
      "data-search-knowledge",
      "automation-operations",
      "software-architecture-engineering",
    ],
  },
];

export function getIndustryBySlug(slug: string): IndustryRecord | undefined {
  return industries.find((industry) => industry.slug === slug);
}
